import { ClientEvents } from 'common/events/ClientEvents';
import React, { useEffect, useRef } from 'react';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { useRecordConsentDecisionMutation } from 'src/gqlReactTypings.generated.d';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useCcpaConsentStoreSelectors } from 'src/ztore/ccpa-consent-store';

export const CcpaConsentBanner: React.FC = () => {
  const isConsentBannerShowing = useCcpaConsentStoreSelectors.use.isConsentBannerShowing();
  const isFlagEnabled = useFeatureFlag('ccpaConsentBannerEnabled');
  const { acceptAll, showCustomizeModal } = useCcpaConsentStoreSelectors.getState();
  const [recordConsent] = useRecordConsentDecisionMutation();
  const tracker = useTracker();
  const hasTrackedShown = useRef(false);

  // Ref intentionally resets on page reload — we want to track impressions per page view, not once-ever
  useEffect(() => {
    if (isConsentBannerShowing && isFlagEnabled && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      tracker.track(ClientEvents.CCPA_CONSENT_BANNER_SHOWN, {});
    }
  }, [isConsentBannerShowing, isFlagEnabled, tracker]);

  if (!isConsentBannerShowing || !isFlagEnabled) return null;

  const handleAcceptAll = () => {
    tracker.track(ClientEvents.CCPA_CONSENT_ACCEPTED, {});
    recordConsent({ variables: { consentType: 'ADVERTISING', decision: 'ACCEPTED' } }).catch(console.error);
    acceptAll();
    const success = tracker.enableAdTracking?.() ?? false;
    if (success) {
      tracker.track(ClientEvents.CCPA_AD_TRACKERS_INITIALIZED, {});
    }
  };

  const handleCustomize = () => {
    tracker.track(ClientEvents.CCPA_CUSTOMIZE_CLICKED, { location: 'banner' });
    showCustomizeModal();
  };

  return (
    <div className='fixed inset-x-0 bottom-0 z-50 border-t border-eggplant-100 bg-white p-4 shadow-lg md:px-8'>
      <div className='mx-auto flex max-w-5xl flex-col items-start gap-3 md:flex-row md:items-center md:justify-between'>
        <div className='flex-1'>
          <p className='text-sm font-semibold text-eggplant-600'>Your Privacy</p>
          <p className='mt-1 text-sm text-neutral-800'>
            We use cookies and tracking technologies for personalized advertising. Under California law, you can choose
            whether to allow this.{' '}
            <a
              href='/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
              className='text-eggplant-600 underline hover:text-radish-200'>
              Learn more
            </a>
          </p>
        </div>
        <div className='flex shrink-0 gap-3'>
          <button
            type='button'
            onClick={handleCustomize}
            className='rounded-lg border border-eggplant-300 px-5 py-2.5 text-sm font-semibold text-eggplant-600 hover:bg-eggplant-50'>
            Customize
          </button>
          <button
            type='button'
            onClick={handleAcceptAll}
            className='hover:bg-eggplant-700 rounded-lg bg-eggplant-600 px-5 py-2.5 text-sm font-semibold text-white'>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};
